import {
  createContext,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface RouterLocation {
  pathname: string;
  search: string;
  state: unknown;
}

interface NavigateOptions {
  replace?: boolean;
  state?: unknown;
}

type NavigateFunction = (to: string, options?: NavigateOptions) => void;

interface RouterValue {
  location: RouterLocation;
  navigate: NavigateFunction;
}

const RouterContext = createContext<RouterValue | null>(null);
const ParamsContext = createContext<Readonly<Record<string, string>>>({});

function readLocation(): RouterLocation {
  const historyState: unknown = window.history.state;
  const state =
    typeof historyState === "object" &&
    historyState !== null &&
    "mongmeeState" in historyState
      ? historyState.mongmeeState
      : null;
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    state,
  };
}

export function validInternalTarget(to: string): string {
  const base = new URL("https://mongmee.invalid/");
  const resolved = new URL(to, base);
  if (
    !to.startsWith("/") ||
    to.startsWith("//") ||
    /[\\\r\n]/u.test(to) ||
    /%5c/iu.test(to) ||
    resolved.origin !== base.origin
  ) {
    throw new Error("Navigation target must be an internal absolute path.");
  }
  return to;
}

export function BrowserRouter({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<RouterLocation>(readLocation);

  useEffect(() => {
    const onPopState = () => setLocation(readLocation());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback<NavigateFunction>((target, options = {}) => {
    const to = validInternalTarget(target);
    const historyState = { mongmeeState: options.state ?? null };
    if (options.replace) {
      window.history.replaceState(historyState, "", to);
    } else {
      window.history.pushState(historyState, "", to);
    }
    setLocation(readLocation());
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const value = useMemo(() => ({ location, navigate }), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function ParamsProvider({
  params,
  children,
}: {
  params: Readonly<Record<string, string>>;
  children: ReactNode;
}) {
  return <ParamsContext.Provider value={params}>{children}</ParamsContext.Provider>;
}

export function useLocation(): RouterLocation {
  return useRouter().location;
}

export function useNavigate(): NavigateFunction {
  return useRouter().navigate;
}

export function useParams(): Readonly<Record<string, string | undefined>> {
  return useContext(ParamsContext);
}

export function useSearchParams(): readonly [URLSearchParams] {
  const { search } = useLocation();
  return useMemo(() => [new URLSearchParams(search)] as const, [search]);
}

export function Navigate({
  to,
  replace = false,
  state,
}: {
  to: string;
  replace?: boolean;
  state?: unknown;
}) {
  const navigate = useNavigate();
  useEffect(() => navigate(to, { replace, state }), [navigate, replace, state, to]);
  return null;
}

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  to: string;
}

export function Link({ to, onClick, ...props }: LinkProps) {
  const navigate = useNavigate();
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    navigate(to);
  }
  return <a {...props} href={validInternalTarget(to)} onClick={handleClick} />;
}

export function NavLink(props: LinkProps) {
  const { pathname, search } = useLocation();
  const target = new URL(props.to, "https://mongmee.invalid/");
  const active = pathname === target.pathname && (!target.search || search === target.search);
  return <Link {...props} aria-current={active ? "page" : undefined} />;
}

function useRouter(): RouterValue {
  const router = useContext(RouterContext);
  if (!router) throw new Error("BrowserRouter is missing.");
  return router;
}
